import { LayoutGrid, Palette, Wand2, BarChart3, LogOut, User, Clock, ChevronRight } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../hooks/useCredits';
import { isFeatureEnabled } from '../../lib/featureFlags';
import { cn } from '../../lib/cn';

interface SidebarProps {
  onUpgradeClick: () => void;
}

type NavItem = {
  icon: typeof LayoutGrid;
  label: string;
  href: string;
  badge?: boolean;
};

export function Sidebar({ onUpgradeClick }: SidebarProps) {
  const { profile, signOut } = useAuth();
  const { credits, creditsIncluded, subscriptionUsage } = useCredits();
  const location = useLocation();
  const navigate = useNavigate();
  const showSubscriptionWidget = false;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems: NavItem[] = [
    { icon: LayoutGrid, label: 'Projects', href: '/dashboard' },
    { icon: Clock, label: 'Results', href: '/dashboard?tab=results' },
    { icon: Palette, label: 'Style Library', href: '/styles' },
    ...(isFeatureEnabled('magicEditor') ? [{ icon: Wand2, label: 'Magic Editor', href: '/editor', badge: true }] : []),
    ...(isFeatureEnabled('analytics') ? [{ icon: BarChart3, label: 'Analytics', href: '/analytics' }] : []),
  ];

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      if (location.pathname.startsWith('/project')) return true;
      if (location.pathname !== '/dashboard') return false;
      return !location.search.includes('tab=results') && !location.search.includes('tab=staging_jobs');
    }
    if (href.startsWith('/dashboard?tab=results')) {
      return location.pathname === '/dashboard'
        && (location.search.includes('tab=results') || location.search.includes('tab=staging_jobs'));
    }
    return location.pathname.startsWith(href);
  };

  const maxCredits = creditsIncluded > 0 ? creditsIncluded : (profile?.credits ?? 100);
  const subscription = subscriptionUsage?.subscription ?? null;
  const plan = subscriptionUsage?.plan ?? null;
  const isPastDue = subscription?.status === 'past_due';
  const isScheduledToCancel = Boolean(subscription?.cancel_at_period_end && subscription?.scheduled_cancellation_date);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const statusMeta = (() => {
    switch (subscription?.status) {
      case 'active':
        return { label: 'Active', badge: 'border-olive/20 bg-olive/10 text-olive' };
      case 'trial':
        return { label: 'Trial', badge: 'border-olive/20 bg-olive/10 text-olive' };
      case 'past_due':
        return { label: 'Past Due', badge: 'border-terracotta/20 bg-terracotta/10 text-terracotta' };
      default:
        return { label: 'Free', badge: 'border-stone bg-stone/20 text-sand' };
    }
  })();

  const subscriptionContext = (() => {
    if (!subscription) return 'Use credits to stage photos instantly.';
    if (isPastDue) return 'Payment failed. Update billing details to avoid cancellation.';
    if (isScheduledToCancel) {
      return `Cancels on ${formatDate(subscription.scheduled_cancellation_date) || 'the end of period'}.`;
    }
    return `Renews ${formatDate(subscription.current_period_end) || 'soon'}.`;
  })();

  const handleSubscriptionAction = () => {
    if (!subscription) {
      onUpgradeClick();
      return;
    }

    navigate('/profile?tab=subscription');
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((name) => name[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <aside className="hidden h-dvh w-72 shrink-0 border-r border-stone bg-white md:flex md:flex-col">
      <div className="flex h-full flex-col px-5 py-5">
        <Link to="/dashboard" className="flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors duration-150 hover:bg-stone/20">
          <img
            src="/AppImages/ios/40.png"
            alt="VirtualStagePro"
            className="size-9 rounded-xl border border-stone object-cover"
          />
          <div className="min-w-0">
            <p className="text-xs text-sand">VirtualStagePro</p>
            <p className="truncate text-sm font-semibold text-charcoal">Workspace</p>
          </div>
        </Link>

        <div className="mt-8 px-3">
          <p className="text-xs uppercase text-sand">Navigation</p>
        </div>

        <nav className="mt-3 space-y-1">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.label}
                to={item.href}
                className={cn(
                  'group flex items-center gap-3 rounded-xl border px-3 py-3 text-sm transition-colors duration-150',
                  active
                    ? 'border-olive/20 bg-olive/10 text-charcoal'
                    : 'border-transparent text-sand hover:border-stone hover:bg-stone/20 hover:text-charcoal'
                )}
              >
                <div
                  className={cn(
                    'flex size-9 items-center justify-center rounded-lg border transition-colors duration-150',
                    active
                      ? 'border-olive/20 bg-white text-olive'
                      : 'border-stone bg-white text-sand group-hover:text-charcoal'
                  )}
                >
                  <item.icon className="size-4" />
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <span className="truncate font-medium">{item.label}</span>
                  {item.badge ? (
                    <span className="size-2 rounded-full bg-terracotta" />
                  ) : active ? (
                    <ChevronRight className="size-4 text-olive" />
                  ) : null}
                </div>
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto space-y-4 pt-6">
          {showSubscriptionWidget && (
            <div className="rounded-2xl border border-stone bg-alabaster p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="truncate text-sm font-semibold text-charcoal">{plan?.name || 'Free Plan'}</p>
                <span className={cn('rounded-full border px-2 py-1 text-[11px]', statusMeta.badge)}>
                  {statusMeta.label}
                </span>
              </div>
              <p className="mt-2 text-xs text-sand text-pretty">
                {credits}/{maxCredits} credits
              </p>
              <p className="mt-2 text-xs text-sand text-pretty">{subscriptionContext}</p>
              <button
                onClick={handleSubscriptionAction}
                className="mt-4 inline-flex w-full items-center justify-center rounded-xl border border-stone bg-white px-3 py-2 text-sm font-medium text-charcoal transition-colors duration-150 hover:bg-stone/20"
                type="button"
              >
                {isPastDue ? 'Update Payment' : subscription ? 'Manage Subscription' : 'Upgrade'}
              </button>
              {subscription && !isPastDue && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-2 inline-flex w-full items-center justify-center rounded-xl border border-stone bg-transparent px-3 py-2 text-sm text-sand transition-colors duration-150 hover:bg-white"
                  type="button"
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          )}

          <Link
            to="/profile"
            className="flex items-center gap-3 rounded-2xl border border-stone bg-alabaster px-3 py-3 transition-colors duration-150 hover:bg-stone/20"
          >
            <div className="flex size-11 items-center justify-center rounded-xl bg-charcoal text-sm font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-charcoal">{profile?.full_name || 'User'}</p>
              <p className="truncate text-xs text-sand">{profile?.email}</p>
            </div>
            <User className="size-4 text-sand" />
          </Link>

          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-terracotta transition-colors duration-150 hover:bg-terracotta/5"
            type="button"
          >
            <LogOut className="size-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
